import { ref } from "vue";
// Type-only, and it has to stay that way: references.ts imports this module for
// its lookups, so a value import here would close the cycle at runtime.
import type { Blueprint, BlueprintMeta, BlueprintSummary, BuilderGraph, BuilderPalette, ErpnextStatus, FilePreview, GraphProblem, OutboundPolicy, SecretInfo, TryResult, Customised, Revision } from "./agentBuilder";
import type { ChatReference } from "./references";
import type { FormLayout } from "./chatForms";
// Type-only for the same reason: dashboardBuilder's own imports reach this module.
import type { ReportTile } from "./dashboardBuilder";
import { analyticsQuery } from "./requestAnalytics";
import { apiUrl } from "./backend";
import { pageViewHeaders } from "./pageView";
import { flagErpReauthNeeded } from "./erpSignIn";
import type {
  IntegrationConnection,
  IntegrationOverview,
  IntegrationProvider,
  IntegrationScope,
  AgentDecision,
  AgentDefDetail,
  AgentDetail,
  AgentGallery,
  AgentInstallConfig,
  AgentInstance,
  AgentNote,
  AgentNoteKind,
  AgentProposal,
  AgentRun,
  ChannelEndpoint,
  ChannelKind,
  ChannelPolicy,
  InboundMessage,
  OutboundMessage,
  ActiveConnection,
  Automation,
  AutomationConfig,
  AutomationGallery,
  AutomationPreview,
  AutomationRun,
  AutomationRunResult,
  AutomationTemplateDetail,
  DashboardPayload,
  DeskDashboard,
  DeskPulse,
  DescribedCard,
  LookupOption,
  AdminConnection,
  SiteQuestion,
  AdminOps,
  AdminQuality,
  DocumentAskField,
  DocumentRead,
  DocumentExtraction,
  ReportIndex,
  ReportInspection,
  ReportsOverview,
  SiteReportDefinition,
  EngineCatalogue,
  ChatOutcome,
  ChatQueued,
  ConnectionDraft,
  ConversationTrace,
  Conversation,
  CompanionContext,
  ConversationFilter,
  ErpAction,
  IntakeDocument,
  InboxAnalysis,
  InboxAttachment,
  InboxCounts,
  InboxFolder,
  InboxFullMessage,
  InboxMessage,
  KnowledgeItem,
  MessageVersion,
  SkillDetail,
  SkillDraft,
  SkillRedraft,
  SkillTestRun,
  PlaybookImpact,
  PlaybookRun,
  SkillPage,
  Profile,
  AnalyticsPageLoad,
  AnalyticsQuery,
  AnalyticsThread,
  RequestEvent,
  RequestExport,
  RequestFilters,
  RequestMetrics,
  User,
  LearningSettings,
  LearningSuggestion,
  KnowledgeConflict,
  KnowledgeVersion,
  Influence,
  Verification,
  FeedbackValue,
  SkillEvaluation,
  LearningReport,
  SkillMeta,
  Message,
  RecallNudge,
  RecallQuery,
  RecallSyncStatus,
  ThreadRecap,
} from "./types";

const TOKEN_KEY = "genie.token";
const REFRESH_KEY = "genie.refresh";
/** The most recent sign-in in this browser — what a new tab starts from. */
const LAST_KEY = "genie.last";

/**
 * Each tab holds its own session, in sessionStorage.
 *
 * Operators of different ERP sites — or two people at one desk — sign in side
 * by side in one browser. A session shared through localStorage made every
 * sign-in take over every other tab: tab one, signed in to one site, quietly
 * became tab two's account on another. sessionStorage is per tab and survives
 * a reload, which is exactly the lifetime a tab's session wants.
 */
function tabStored(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function tabStore(key: string, value: string | null): void {
  try {
    if (value) sessionStorage.setItem(key, value);
    else sessionStorage.removeItem(key);
  } catch {
    // Blocked storage: the session lives for this page only.
  }
}

function lastSignIn(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

function rememberLast(value: string | null): void {
  try {
    if (value) localStorage.setItem(LAST_KEY, value);
    else localStorage.removeItem(LAST_KEY);
  } catch {
    // Nothing for a new tab to start from; it signs in instead.
  }
}

/**
 * The session a browser held before tabs had their own. Its refresh token
 * becomes the last sign-in, so the next page load forks from it rather than
 * signing an operator out across an upgrade. Both the genie.* and the older
 * jarvis.* names are read.
 */
function carryOverShared(): void {
  try {
    const legacy =
      localStorage.getItem(REFRESH_KEY) ?? localStorage.getItem("jarvis.refresh");
    if (legacy && !lastSignIn()) rememberLast(legacy);
    for (const key of [TOKEN_KEY, REFRESH_KEY, "jarvis.token", "jarvis.refresh"]) {
      localStorage.removeItem(key);
    }
  } catch {
    /* blocked storage: nothing to carry */
  }
}
carryOverShared();

export const token = ref<string | null>(tabStored(TOKEN_KEY));
export const refreshToken = ref<string | null>(tabStored(REFRESH_KEY));
export const currentUser = ref<User | null>(null);

/** A WhatsApp template as Meta approved it: its name there, its language, its merge fields in order. */
export interface WhatsAppTemplate {
  name: string;
  language: string;
  fields: string[];
}

export function setToken(value: string | null): void {
  token.value = value;
  tabStore(TOKEN_KEY, value);
}

/**
 * This tab's session, both halves together — a stale refresh token paired with
 * a fresh access token is a session that dies silently 15 minutes later.
 *
 * Called on sign-in and sign-out. A sign-in also becomes the last one, so a
 * tab opened next starts as this operator; signing out forgets the last one
 * only when it was this tab's.
 */
export function setSession(value: { token: string; refreshToken?: string } | null): void {
  const previous = refreshToken.value;
  keepSession(value);
  if (value?.refreshToken) rememberLast(value.refreshToken);
  else if (previous && lastSignIn() === previous) rememberLast(null);
}

function keepSession(value: { token: string; refreshToken?: string } | null): void {
  freshTab = false;
  setToken(value?.token ?? null);
  refreshToken.value = value?.refreshToken ?? null;
  tabStore(REFRESH_KEY, refreshToken.value);
}

/**
 * A tab with no session of its own starts from the last sign-in — forked, so
 * it gets an independent chain. Sharing the chain would have the two tabs
 * rotate one token, and the slower one present a retired token and end both.
 * The route guard awaits this before deciding the tab is signed out.
 */
let forking: Promise<boolean> | null = null;
/**
 * Only a tab that has never held a session forks. One whose session ended goes
 * to the sign-in screen: picking up the last sign-in then would turn it, mid
 * page, into whichever operator signed in most recently in another tab.
 */
let freshTab = !token.value && !refreshToken.value;

export function ensureSession(): Promise<boolean> {
  if (token.value || refreshToken.value) return Promise.resolve(true);
  const last = freshTab ? lastSignIn() : null;
  freshTab = false;
  if (!last) return Promise.resolve(false);
  forking ??= (async () => {
    try {
      const response = await fetch(apiUrl("/api/auth/fork"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ refreshToken: last }),
      });
      if (!response.ok) {
        // That sign-in has ended; nothing for any new tab to start from.
        if (response.status === 401 && lastSignIn() === last) rememberLast(null);
        return false;
      }
      keepSession((await response.json()) as { token: string; refreshToken: string });
      return true;
    } catch {
      return false;
    } finally {
      forking = null;
    }
  })();
  return forking;
}

/** Forget this tab's session. Other tabs keep theirs. */
function dropSession(): void {
  setSession(null);
}

/**
 * One refresh at a time per tab. A page with several panels can easily fire
 * five requests that all 401 at once; each renewing on its own rotated the
 * refresh token under the others, and the server read the replay as theft and
 * revoked the whole family — logging the operator out for being busy.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function renewOnce(): Promise<boolean> {
  if (!refreshToken.value) return false;
  const presented = refreshToken.value;
  try {
    const response = await fetch(apiUrl("/api/auth/refresh"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ refreshToken: presented }),
    });
    if (!response.ok) {
      // Only the server saying this token is no good ends the session. A restart in progress (a proxy's
      // 502/503/504), a rate limit (429) or any other failure says nothing about the token, and dropping
      // it there signed people out in the middle of a question for the sake of a few seconds of outage.
      if (response.status === 400 || response.status === 401 || response.status === 403) dropSession();
      return false;
    }
    const payload = (await response.json()) as { token: string; refreshToken: string };
    keepSession(payload);
    // A new tab must fork from a live token, not the one just retired.
    if (lastSignIn() === presented) rememberLast(payload.refreshToken);
    return true;
  } catch {
    // A network failure is not proof the session ended; keep the tokens and
    // let the caller surface the error, so a brief outage does not sign the
    // operator out.
    return false;
  }
}

async function refreshSession(): Promise<boolean> {
  if (!refreshToken.value) return false;
  if (refreshInFlight) return refreshInFlight;
  const pending = renewOnce().finally(() => {
    refreshInFlight = null;
  });
  refreshInFlight = pending;
  return pending;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string
  ) {
    super(message);
  }
}

/**
 * The operator stopped this request themselves.
 *
 * Kept apart from ApiError because the UI must treat it as the opposite thing:
 * nothing failed, so no error banner, no retry prompt and no "the server is
 * unreachable" — the turn simply ended early, and the server has already landed
 * it in the transcript as interrupted.
 */
export class RequestAborted extends Error {
  readonly aborted = true;
  constructor() {
    super("Stopped.");
    this.name = "RequestAborted";
  }
}

export function isAborted(err: unknown): err is RequestAborted {
  return err instanceof RequestAborted;
}

/**
 * Both deadlines matter, so neither replaces the other: without the timeout a
 * wedged server leaves the composer stuck on "sending" forever, and without the
 * caller's signal Stop has nothing to pull. Whichever fires first wins.
 */
function untilEither(signal: AbortSignal | undefined, timeoutMs?: number): AbortSignal | undefined {
  const deadline = timeoutMs ? AbortSignal.timeout(timeoutMs) : undefined;
  if (!signal) return deadline;
  return deadline ? AbortSignal.any([signal, deadline]) : signal;
}

/**
 * The half of an attached record that the server has any business knowing.
 *
 * A ChatReference also carries `id`, `kind`, `title` and `hint` — chip layout,
 * de-duplication, a picker subtitle — all of which the composer is free to
 * reword tomorrow. Sending them would invite the model prompt to start
 * depending on presentation. `label` crosses because the note names the record
 * to the model the way the operator saw it; everything else is identifiers.
 */
function onTheWire(reference: ChatReference): Record<string, string | undefined> {
  return {
    label: reference.label,
    referenceType: reference.referenceType,
    doctype: reference.doctype,
    documentName: reference.documentName,
    parameterId: reference.parameterId,
    parameterValue: reference.parameterValue,
    source: reference.source,
  };
}

/**
 * A response body that claims to be JSON but isn't — an HTML error page from a
 * gateway timeout, a restarting dev server, or the ERP site itself — must
 * become an honest ApiError, never a raw `Unexpected token '<'` in the chat.
 */
function parseJsonBody(
  text: string,
  response: Response
): { message?: string; error?: string } | null {
  if (!text) return null;
  try {
    return JSON.parse(text) as { message?: string; error?: string };
  } catch {
    throw new ApiError(
      response.ok
        ? "The server replied with a non-JSON page — it may be restarting, or a proxy in front of it timed out. The work may still have finished; check the transcript in a moment, or try again."
        : `Request failed (${response.status}): the server returned an error page instead of JSON.`,
      response.ok ? 502 : response.status
    );
  }
}

async function request<T>(
  path: string,
  init: {
    method?: string;
    body?: unknown;
    auth?: boolean;
    timeoutMs?: number;
    /** Caller's cancellation — the Stop button. Combined with timeoutMs. */
    signal?: AbortSignal;
    /** Internal: set when this is the post-refresh retry, so it cannot loop. */
    retried?: boolean;
  } = {}
): Promise<T> {
  const hasBody = init.body !== undefined;
  const headers: Record<string, string> = { Accept: "application/json" };
  // Only declare a JSON body when there actually is one — Fastify rejects a
  // request that claims application/json but carries nothing
  // (FST_ERR_CTP_EMPTY_JSON_BODY), which bodyless POSTs like
  // /api/realtime/token and /api/reset would otherwise trip.
  if (hasBody) headers["Content-Type"] = "application/json";
  if (init.auth !== false && token.value) headers.Authorization = `Bearer ${token.value}`;
  // Which page load this call belongs to, so request analytics can list the
  // calls each page made (lib/pageView.ts).
  Object.assign(headers, pageViewHeaders());

  let response: Response;
  try {
    response = await fetch(apiUrl(path), {
      method: init.method ?? "GET",
      headers,
      body: hasBody ? JSON.stringify(init.body) : undefined,
      // Long-running calls (chat) opt into a deadline so a wedged server
      // surfaces an error banner instead of a composer stuck on "sending".
      signal: untilEither(init.signal, init.timeoutMs),
    });
  } catch (err) {
    // Checked before the timeout: when the operator stops a request that was
    // also near its deadline, what they did is the true explanation, and the
    // difference decides whether the UI apologizes or stays quiet.
    if (init.signal?.aborted) throw new RequestAborted();
    if (err instanceof DOMException && err.name === "TimeoutError") {
      throw new ApiError(
        "The server took too long to reply. It may still be working — check the transcript in a moment, or try again.",
        504
      );
    }
    throw err;
  }

  const text = await response.text();
  const payload = parseJsonBody(text, response);

  if (!response.ok) {
    // An expired access token is the ordinary case now, not a sign-out: try
    // once to renew and replay the request. Only a failed renewal ends the
    // session.
    if (response.status === 401 && init.auth !== false && !init.retried) {
      if (await refreshSession()) {
        return request<T>(path, { ...init, retried: true });
      }
      // renewOnce has already ended the session if the server refused the token. If it only could not
      // be reached, the session stays and the request fails on its own, to be tried again.
      if (!refreshToken.value) dropSession();
    } else if (response.status === 401) {
      dropSession();
    }
    throw new ApiError(
      payload?.message ?? `Request failed (${response.status})`,
      response.status,
      payload?.error
    );
  }
  return payload as T;
}

/**
 * Every ERP tool answers through this shape, and only here does the client
 * see the tool's own error code rather than an HTTP status — ERPNext
 * refusing the credential is not a failed request to Genie's own server, so
 * it never reaches the 401 handling above. Every screen that reads a tool
 * result passes through `executeTool` or `executeToolQuiet`, so checking here
 * once catches it wherever it happens rather than in each screen that calls
 * an ERP tool.
 */
function watchForExpiredErp<T extends Record<string, unknown>>(result: T): T {
  if (result?.error === "unauthenticated") {
    flagErpReauthNeeded(typeof result.message === "string" ? result.message : "ERPNext did not accept Genie's sign-in for this user.");
  }
  return result;
}

/** `?tag=..&since=..&until=..` — empty string when nothing narrows. */
function filterQuery(filter: ConversationFilter, limit?: number, offset?: number): string {
  const p = new URLSearchParams();
  if (filter.tag) p.set("tag", filter.tag);
  if (filter.since) p.set("since", String(filter.since));
  if (filter.until) p.set("until", String(filter.until));
  if (limit) p.set("limit", String(limit));
  if (offset) p.set("offset", String(offset));
  const s = p.toString();
  return s ? `?${s}` : "";
}


/** One field of a waiting document, carrying the doctype's own label and type for it. */
export interface ApprovalField {
  field: string;
  /** The doctype's own label, so the queue never invents a heading for a custom field. */
  label: string;
  value: unknown;
  /** The fieldtype, which is better evidence than a fieldname for how to render the value. */
  type: string | null;
}

/** One thing waiting on an approver, whatever doctype it came from. */
export interface ApprovalItem {
  doctype: string;
  name: string;
  /** The doctype's title_field value; null when it has none or when it only repeats `name`. */
  title: string | null;
  person: string | null;
  personId: string | null;
  /**
   * What `personId` points at. The queue mixes doctypes, so the same column
   * holds an Employee on one row and a Supplier on the next, and an id alone
   * cannot be looked up or grouped without knowing which table it came from.
   */
  personDoctype: string | null;
  date: string | null;
  amount: number | null;
  /** This row's own currency, so a total is never summed across two of them. */
  currency: string | null;
  owner: string | null;
  waitingSince: string | null;
  /** "workflow" when ERPNext addressed it to this person; "decision" when inferred from an undecided draft. */
  source: "workflow" | "decision";
  state: string | null;
  actions: { id: string; label: string; irreversible: boolean }[];
  /** Why nothing may be done to it from here, when that is the case. */
  blocked: string | null;
  /** Ordered, labelled and ready to render as columns. */
  fields: ApprovalField[];
}

export interface ApprovalQueue {
  count: number;
  items: ApprovalItem[];
  doctypes: { doctype: string; count: number }[];
  /** Doctypes that could not be read, so a short queue is never mistaken for an empty one. */
  skipped: { doctype: string; reason: string }[];
  workflowError: string | null;
  erpUser: string | null;
  /**
   * Per doctype: how many rows were read, whether the site holds more, and the
   * site's own total for the filtered set when it reported one.
   *
   * `total` is the difference between "100 read, more waiting" and "100 of
   * 3,412", and only the second lets an approver judge whether to page through
   * or go and filter. It stays nullable because a lane can know it left rows
   * behind without knowing how many.
   */
  truncated: { doctype: string; read: number; more: boolean; total: number | null }[];
  /**
   * The columns each doctype contributes, so a group of one doctype can draw
   * its headers without inspecting the rows underneath them.
   */
  columns: { doctype: string; fields: { field: string; label: string; type: string | null }[] }[];
  /** Every doctype the read looked at, with rows or without — the document-type filter lists them all. */
  scanned?: string[];
  /** Workflow rows the queue was read without buttons for, when asked to defer them. */
  pendingActions?: { doctype: string; rows: { name: string; state: string | null }[] }[];
}

/** How the console asks for its view of the queue: the piles it wants and how each is narrowed. */
export interface ApprovalViewState {
  dimension: "person" | "period" | "doctype" | "age" | "state" | "source" | `field:${string}`;
  search: string;
  chosen: Record<string, string[]>;
  piles: Record<string, { query?: string; rules?: unknown[]; sort?: { key: string; dir: "asc" | "desc" } }>;
  /** The operator's calendar day, for relative dates in a pile's conditions. */
  today: string;
}

/** One pile as the server summarises it: everything but its rows. */
export interface ApprovalPileSummary {
  key: string;
  label: string;
  count: number;
  memberCount: number;
  query: string;
  rules: import("./listFilters").FilterRow[];
  fields: import("./approvalFilters").FilterField[];
  narrowed: boolean;
  total: number | null;
  currency: string | null;
  mixedCurrency: boolean;
  doctype: string | null;
  columns: { field: string; label: string; type: string | null }[];
  actions: { id: string; label: string; irreversible: boolean }[];
  decidable: number;
  owed: boolean;
  ownCount: number;
  oldest: number | null;
  /** Days since the most recent arrival: what the piles are ordered by. */
  newest?: number | null;
}

export interface ApprovalView {
  version: number;
  /** When the server read the queue from the site. */
  readAt: number;
  /** Answered from an older read while the site is read again; ask once more for the fresh one. */
  refreshing?: boolean;
  count: number;
  filteredCount: number;
  selectedHidden: number;
  erpUser: string | null;
  skipped: { doctype: string; reason: string }[];
  workflowError: string | null;
  truncated: ApprovalQueue["truncated"];
  scanned: string[];
  facets: { id: string; label: string; hint: string; options: { id: string; label: string; count: number }[] }[];
  piles: ApprovalPileSummary[];
}

/** Ask for one doctype's next page. Only meaningful together with `doctype`. */
export interface ApprovalPage {
  /** Re-run the site's doctype discovery rather than serving it from cache: the refresh button. */
  fresh?: boolean;
  /**
   * Read the queue without the workflow buttons, which are one ERP read per
   * row and most of the wait; the answer names the rows still owed them
   * (`pendingActions`) and `approvalTransitions` fills them in.
   */
  deferActions?: boolean;
  doctype?: string;
  /** Rows of that doctype to skip. */
  offset?: number;
  /** Page size. The server caps it, so a large number is a request and not a promise. */
  limit?: number;
}

/** OpenAI/Codex auth state for the Settings → AI account section. Never carries the raw key. */
export interface OpenAiAuthStatus {
  textAuth: "api-key" | "chatgpt";
  hasKey: boolean;
  keyFromEnv: boolean;
  hint: string | null;
  subscriptionConnected: boolean;
  available: boolean;
}

/** Claude auth state for the Settings → AI account section. Never carries the raw key. */
export interface ClaudeAuthStatus {
  mode: "auto" | "cli" | "api";
  effectiveMode: "cli" | "api";
  hasKey: boolean;
  keyFromEnv: boolean;
  hint: string | null;
  cliInstalled: boolean;
  available: boolean;
}

export const api = {
  companionStatus: () => request<import("./companion").CompanionStatus>("/api/companion/status"),
  connectCompanionApprovals: () => request<import("./companion").CompanionStatus>("/api/companion/connect-approvals", { method: "POST" }),
  companionActions: (doctype: string, names: string[]) => request<{ lifecycle: string[]; workflow: string[]; count: number }>("/api/companion/actions", { method: "POST", body: { doctype, names }, timeoutMs: 120_000 }),
  companionPreview: (body: { request: import("./companion").OperationRequest; requestId: string; conversationId?: string }) => request<import("./companion").CompanionJob>("/api/companion/preview", { method: "POST", body, timeoutMs: 120_000 }),
  companionJobs: (offset = 0, status = "") => request<{ jobs: import("./companion").CompanionJob[]; unavailableCount: number; hasMore: boolean }>(`/api/companion/jobs?offset=${offset}&limit=20${status ? `&status=${encodeURIComponent(status)}` : ""}`),
  companionContext: (token: string) => request<{ doctype: string; name: string; erp_user: string; document: Record<string, unknown> }>("/api/companion/context", { method: "POST", body: { token } }),
  companionDiagnostics: () => request<Record<string, unknown>>("/api/companion/diagnostics"),
  companionEvents: (after = "") => request<{ events: { event_id: string; doctype: string; name: string; event: string; modified: string; ack_token: string }[]; next_cursor?: string; has_more: boolean }>(`/api/companion/events${after ? `?after=${encodeURIComponent(after)}` : ""}`),
  companionAck: (tokens: string[]) => request("/api/companion/events/ack", { method: "POST", body: { tokens } }),
  companionKnowledge: (query = "", start = 0) => request<{ results: { name: string; title: string; scope: string; tags?: string }[]; next_start: number | null }>(`/api/companion/knowledge?query=${encodeURIComponent(query)}&start=${start}`),
  companionArticle: (name: string) => request<{ title: string; content: string; scope: string }>(`/api/companion/knowledge/${encodeURIComponent(name)}`),
  companionPrepareDocument: (doctype: string, values: Record<string, unknown>) => request<Record<string, unknown>>("/api/companion/prepare-document", { method: "POST", body: { doctype, values } }),
  companionMapDocument: (mapping: string, name: string) => request<Record<string, unknown>>("/api/companion/map-document", { method: "POST", body: { mapping, name } }),
  companionJob: (id: string) => request<import("./companion").CompanionJob>(`/api/companion/jobs/${encodeURIComponent(id)}`),
  approveCompanion: (id: string, payloadHash: string) => request<import("./companion").CompanionJob>(`/api/companion/jobs/${encodeURIComponent(id)}/approve`, { method: "POST", body: { payloadHash } }),
  cancelCompanion: (id: string) => request<import("./companion").CompanionJob | { id: string; detailsUnavailable: true; message: string }>(`/api/companion/jobs/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  verifyCompanion: (companionContext: string, companionSignature: string) =>
    request<{ context: CompanionContext }>(
      `/api/companion/context?companion_context=${encodeURIComponent(companionContext)}&companion_signature=${encodeURIComponent(companionSignature)}`,
      { auth: false },
    ),
  signUp: (email: string, password: string) =>
    request<{ token: string; refreshToken: string; user: User }>("/api/auth/signup", {
      method: "POST",
      body: { email, password },
      auth: false,
    }),

  signIn: (email: string, password: string) =>
    request<{ token: string; refreshToken: string; user: User }>("/api/auth/signin", {
      method: "POST",
      body: { email, password },
      auth: false,
    }),

  /**
   * Sign in AS an ERPNext user. The password goes straight to ERPNext, which
   * validates it and returns an API key pair the server seals and keeps; it is
   * never stored here or held in the browser.
   */
  erpLogin: (site: string, username: string, secret: string | { apiKey: string; apiSecret: string }) =>
    request<{
      token: string;
      refreshToken: string;
      user: User;
      erp: { acting_as: string; site: string; full_name?: string; roles?: string[] };
    }>("/api/auth/erp/login", {
      method: "POST",
      // A key pair is how a regular user comes in where ERPNext will not let them mint their own.
      body:
        typeof secret === "string"
          ? { site, username, password: secret }
          : { site, ...(username ? { username } : {}), api_key: secret.apiKey, api_secret: secret.apiSecret },
      auth: false,
    }),

  me: () => request<{ user: User; profile: Profile | null }>("/api/auth/me"),

  /**
   * Whether this deployment offers "Sign in with ERPNext" at all, and which
   * site it will sign into. Read before there is a session, so it is public —
   * and carries only the connection's name, URL and mode, all of which the
   * operator is about to see in the address bar anyway.
   */
  erpAvailable: () =>
    request<{
      available: boolean;
      site: string | null;
      label: string | null;
      mode: "live" | "mock" | null;
      sites: { site: string; label: string; oauth: boolean; active: boolean }[];
    }>("/api/auth/erp/available", { auth: false }),

  /**
   * Which ERP site this session is talking to.
   *
   * Read by every signed-in operator, not just administrators: showing stage
   * data while someone believes they are on production is the failure this
   * whole feature exists to prevent, and the badge that prevents it needs this.
   */
  activeConnection: () =>
    request<{ connection: ActiveConnection | null; isAdmin: boolean }>(
      "/api/settings/connection"
    ),

  /** Whether the active site may be shown inside a frame of the panel, and why not if not. */
  erpFramePolicy: () =>
    request<{ siteUrl: string | null; framable: boolean; reason: string; cached?: boolean }>("/api/erp/frame-policy"),

  /**
   * Read an uploaded document and propose the ERP record it should become.
   *
   * Stateless by design: the bytes are read, extracted and forgotten. Nothing
   * is stored here and nothing is written to the ERP — what comes back is a
   * proposal for the form flow, which is where a human still presses Save.
   */
  readDocument: (file: { fileName: string; contentType: string; contentBase64: string }) =>
    request<DocumentExtraction>("/api/intake/read", { method: "POST", body: file }),
  /** Create a record Read a document proposed, as a Draft. */
  /** Created — or ERPNext's refusal, turned into the questions it is asking. */
  createFromDocument: (doctype: string, values: Record<string, unknown>, from?: { readId?: string | null; index: number }) =>
    request<{ doctype: string; name: string; needsInput?: undefined } | { needsInput: true; message: string; ask: DocumentAskField[] }>(
      "/api/intake/create",
      { method: "POST", body: { doctype, values, ...(from?.readId ? { readId: from.readId, index: from.index } : {}) } }
    ),
  /** The operator's own reads on this site, newest first. */
  documentHistory: () => request<{ reads: DocumentRead[] }>("/api/intake/history"),
  /** Submit a Draft created above: docstatus 0 → 1. */
  submitCreated: (doctype: string, name: string, from?: { readId?: string | null; index: number }) =>
    request<{ doctype: string; name: string; status?: string }>("/api/intake/submit", {
      method: "POST",
      body: { doctype, name, ...(from?.readId ? { readId: from.readId, index: from.index } : {}) },
    }),

  /** The questions answered without a model round, verified server-side. */
  engineCatalogue: () => request<EngineCatalogue>("/api/engine/catalogue"),
  siteCommands: () =>
    request<{ checked: boolean; modules: import("@/lib/commands").SiteModule[] }>("/api/erp/site-commands"),

  /** "Try asking", built on the server from what the connected site holds. */
  homeSuggestions: () =>
    request<{ suggestions: { text: string; hint: string; source: string }[] }>("/api/home/suggestions"),

  /** The operator's own past questions, newest first, for the composer typeahead. */
  historyQuestions: () => request<{ questions: string[] }>("/api/history/questions"),

  /* ---- the connected site's curated question set ---- */
  siteQuestions: () => request<{ questions: SiteQuestion[] }>("/api/site/questions"),
  createSiteQuestion: (text: string) =>
    request<{ question: SiteQuestion; questions: SiteQuestion[] }>("/api/site/questions", {
      method: "POST",
      body: { text },
    }),
  updateSiteQuestion: (id: string, text: string) =>
    request<{ question: SiteQuestion; questions: SiteQuestion[] }>(`/api/site/questions/${id}`, {
      method: "PUT",
      body: { text },
    }),
  deleteSiteQuestion: (id: string) =>
    request<{ questions: SiteQuestion[] }>(`/api/site/questions/${id}`, { method: "DELETE" }),
  importSiteQuestions: (questions: string[]) =>
    request<{ questions: SiteQuestion[] }>("/api/site/questions/import", {
      method: "POST",
      body: { questions },
    }),

  /* ---- administrator only; every one of these is gated server-side ---- */

  /** Queue health, dead jobs, unresolved writes and the emergency brake. */
  adminOps: () => request<AdminOps>("/api/admin/ops"),

  /** Conversation quality over the trailing window — engine share, stop
   *  reasons, decline codes and the rephrase rate. */
  adminQuality: () => request<AdminQuality>("/api/admin/quality"),

  /** The site's reports, where each comes from, and which answer questions. */
  adminReports: () => request<ReportsOverview>("/api/admin/reports"),

  /** Read the site's reports again — after an app is installed or a report built. */
  refreshReportIndex: () =>
    request<{ index: ReportIndex }>("/api/admin/reports/refresh", { method: "POST" }),

  /**
   * Run a report once: its real columns and, with a figure named, a dry run of
   * it. Filter names are filled with what a question would send — today, the
   * start of the year, the site's company.
   */
  inspectReport: (body: {
    report: string;
    field?: string;
    op?: SiteReportDefinition["aggregation"];
    reportFilters?: SiteReportDefinition["reportFilters"];
    reportDefaults?: Record<string, string | number | boolean>;
  }) => request<ReportInspection>("/api/admin/reports/inspect", { method: "POST", body }),

  /** Save a site definition. The server dry-runs it first and refuses one that could not answer. */
  createSiteReportDefinition: (body: {
    report: string;
    label: string;
    aliases: string[];
    valueField: string | null;
    aggregation: SiteReportDefinition["aggregation"];
    unit: string;
    reportFilters: SiteReportDefinition["reportFilters"];
    reportDefaults?: Record<string, string | number | boolean>;
    caveat: string | null;
  }) => request<SiteReportDefinition>("/api/admin/reports/definitions", { method: "POST", body }),

  deleteSiteReportDefinition: (id: string) =>
    request<{ ok: true }>(`/api/admin/reports/definitions/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /* ---------------- request analytics (admin) ---------------- */

  /** Lifecycle events, newest first, a page at a time — pass `nextCursor` back for the next. */
  requestEvents: (filters: RequestFilters) =>
    request<{ events: RequestEvent[]; nextCursor: string | null }>(`/api/observability/events?${analyticsQuery(filters)}`),

  /** Per-user and per-tool figures, daily counts, and whether recording is keeping up. */
  requestMetrics: (filters: RequestFilters) =>
    request<RequestMetrics>(`/api/observability/metrics?${analyticsQuery(filters)}`),

  /** Up to 1,000 events as a file's worth, with a cursor when there are more. Audited. */
  requestExport: (filters: RequestFilters) =>
    request<RequestExport>(`/api/observability/export?${analyticsQuery(filters)}`),

  /** Threads with activity in the range, most recent first. */
  requestThreads: (filters: { from: number; to: number; user?: string; q?: string; cursor?: string }) =>
    request<{ threads: AnalyticsThread[]; nextCursor: string | null }>(
      `/api/observability/threads?${analyticsQuery(filters)}`
    ),

  /** One thread's queries, in the order they were asked. */
  requestThread: (conversationId: string) =>
    request<{ thread: AnalyticsThread; queries: AnalyticsQuery[] }>(
      `/api/observability/threads/${encodeURIComponent(conversationId)}`
    ),

  /** Page loads in the range, most recent first, with the calls each made. */
  requestPages: (filters: { from: number; to: number; user?: string; q?: string; cursor?: string }) =>
    request<{ pages: AnalyticsPageLoad[]; nextCursor: string | null }>(
      `/api/observability/pages?${analyticsQuery(filters)}`
    ),

  /** Every event behind one query, thread or page load, oldest first. Audited. */
  requestTrail: (selection: { turn: string } | { conversation: string } | { page: string }) =>
    request<{ events: RequestEvent[]; truncated: boolean }>(
      `/api/observability/trail?${new URLSearchParams(selection).toString()}`
    ),

  /** Record that a trail was saved to a file — exports are audited, this one included. */
  recordTrailExport: (body: ({ turn: string } | { conversation: string } | { page: string }) & { steps: number }) =>
    request<{ ok: true }>("/api/observability/trail/exported", { method: "POST", body }),

  /** Something the operator did in the browser. Stored as client-reported, never as proof. */
  recordActivity: (body: {
    action: "page_view" | "result_open" | "result_export" | "feedback";
    screen: string;
    conversationId?: string;
    value?: "helpful" | "unhelpful";
  }) => request<{ ok: true }>("/api/observability/activity", { method: "POST", body }),

  /** Flip the emergency read-only brake on or off. */
  setReadOnly: (enabled: boolean) =>
    request<{ emergency_read_only: boolean }>("/api/admin/read-only", {
      method: "POST",
      body: { enabled },
    }),

  adminConnections: () =>
    request<{ connections: AdminConnection[]; activeId: string | null }>(
      "/api/admin/connections"
    ),

  createConnection: (draft: ConnectionDraft) =>
    request<{ connection: AdminConnection }>("/api/admin/connections", {
      method: "POST",
      body: draft,
    }),

  updateConnection: (id: string, draft: ConnectionDraft) =>
    request<{ connection: AdminConnection }>(`/api/admin/connections/${id}`, {
      method: "PATCH",
      body: draft,
    }),

  deleteConnection: (id: string) =>
    request<{ ok: true }>(`/api/admin/connections/${id}`, { method: "DELETE" }),

  /** Ask the ERP site who these credentials authenticate as. */
  testConnection: (id: string) =>
    request<{
      result: { ok: boolean; as?: string; error?: string };
      connection: AdminConnection;
    }>(`/api/admin/connections/${id}/test`, { method: "POST" }),

  /**
   * Switch the live site. Ends every session on the other connections, so the
   * caller is expected to sign itself out when `signOutRequired` comes back.
   */
  activateConnection: (id: string) =>
    request<{
      connection: AdminConnection;
      revokedSessions: number;
      signOutRequired: boolean;
    }>(`/api/admin/connections/${id}/activate`, { method: "POST" }),

  connectionAudit: () =>
    request<{
      entries: {
        id: string;
        connectionId: string | null;
        actorEmail: string;
        action: string;
        detail: string | null;
        createdAt: number;
      }[];
    }>("/api/admin/connections/audit"),

  /** Whether a ChatGPT/OpenAI session is stored, and whose it is. */
  openaiStatus: () =>
    request<{
      connected: boolean;
      email?: string;
      plan?: string;
      authPath: string;
      redirectUri: string;
      textAuth: string;
    }>("/api/auth/openai/status"),

  /** Returns the auth.openai.com URL to navigate the browser to. */
  openaiStart: () => request<{ url: string }>("/api/auth/openai/start", { method: "POST" }),

  openaiDisconnect: () =>
    request<{ connected: false }>("/api/auth/openai/disconnect", { method: "POST" }),

  /** OpenAI/Codex API key + method (subscription vs. API key), set from Settings. */
  openaiKeyStatus: () => request<OpenAiAuthStatus>("/api/auth/openai/key"),
  saveOpenaiKey: (body: { textAuth?: "api-key" | "chatgpt"; apiKey?: string | null }) =>
    request<OpenAiAuthStatus>("/api/auth/openai/key", { method: "PUT", body }),

  /** Claude auth: CLI subscription vs. an Anthropic API key, set from Settings. */
  claudeAuthStatus: () => request<ClaudeAuthStatus>("/api/auth/claude/status"),
  saveClaudeAuth: (body: { mode?: "auto" | "cli" | "api"; apiKey?: string | null }) =>
    request<ClaudeAuthStatus>("/api/auth/claude", { method: "PUT", body }),

  /**
   * Whose ERP identity this operator's actions run as. When not connected,
   * every ERP call still goes through the shared service account — which is
   * exactly what the badge in the UI needs to say out loud.
   */
  erpStatus: () =>
    request<{
      connected: boolean;
      acting_as: string;
      site?: string;
      full_name?: string;
      roles?: string[];
      expires_at?: number;
    }>("/api/auth/erp/status"),

  erpDisconnect: () =>
    request<{ connected: false }>("/api/auth/erp/disconnect", { method: "POST" }),

  saveProfile: (patch: Partial<Profile>) =>
    request<Profile>("/api/profile", { method: "PUT", body: patch }),

  /**
   * How this operator wants one chat form laid out — which standard fields
   * they switched off, which extra doctype fields they added. Per user, so it
   * follows them between browsers rather than living in localStorage.
   */
  formLayout: (formId: string) => request<FormLayout>(`/api/form-prefs/${formId}`),

  /**
   * Parameter sets the operator saved against catalogue commands. Fetched
   * whole rather than per command: the home screen shows every row at once,
   * and one read beats seventy.
   */
  commandPresets: () =>
    request<{ presets: Record<string, { label: string; values: Record<string, string> }[]> }>(
      "/api/command-presets"
    ),

  /** Which of these doctypes the connected site has; `checked: false` when it could not be asked. */
  doctypesPresent: (names: string[], usage = false) =>
    request<{ checked: boolean; present: string[]; used?: string[] }>("/api/erp/doctypes-present", {
      method: "POST",
      body: { names, usage },
    }),

  /** Starred catalogue commands, newest star last. */
  commandFavourites: () => request<{ favourites: string[] }>("/api/command-favourites"),

  /** Star or unstar; the reply says which it ended up as. */
  toggleCommandFavourite: (command: string) =>
    request<{ command: string; favourite: boolean }>(
      `/api/command-favourites/${encodeURIComponent(command)}`,
      { method: "POST" }
    ),

  saveCommandPresets: (
    command: string,
    presets: { label: string; values: Record<string, string> }[]
  ) =>
    request<{ presets: { label: string; values: Record<string, string> }[] }>(
      `/api/command-presets/${encodeURIComponent(command)}`,
      { method: "PUT", body: { presets } }
    ),

  saveFormLayout: (formId: string, layout: FormLayout & { required: string[] }) =>
    request<FormLayout>(`/api/form-prefs/${formId}`, { method: "PUT", body: layout }),

  resetFormLayout: (formId: string) =>
    request<FormLayout>(`/api/form-prefs/${formId}`, { method: "DELETE" }),

  /** The operator's saved views over a doctype's list; built-in ones are derived on the client. */
  listViews: (doctype: string) =>
    request<{ views: unknown[] }>(`/api/list-views/${encodeURIComponent(doctype)}`),

  saveListViews: (doctype: string, views: unknown[]) =>
    request<{ views: unknown[] }>(`/api/list-views/${encodeURIComponent(doctype)}`, {
      method: "PUT",
      body: { views },
    }),

  /** Which agent answers first and which takes over when it fails — each person's own choice. */
  agentSettings: (scope: AgentScope = "chat") =>
    request<AgentSettings>(`/api/agent-settings?scope=${scope}`),
  /** A person's own choice; null for a half means "follow the site's". */
  saveAgentSettings: (value: Partial<AgentLayer>, scope: AgentScope = "chat") =>
    request<AgentSettings>(`/api/agent-settings?scope=${scope}`, { method: "PUT", body: value }),
  /** The default for everyone on the administrator's site; null for a half means Genie's default. */
  saveSiteAgentSettings: (value: Partial<AgentLayer>, scope: AgentScope = "chat") =>
    request<AgentSettings>(`/api/agent-settings/site?scope=${scope}`, { method: "PUT", body: value }),

  /** The look this user should see: theirs, or the organisation's when it is locked or they chose none. */
  appearance: () =>
    request<{ theme: string; overrides?: Record<string, unknown>; source?: "user" | "org" | "default"; locked?: boolean } | null>(
      "/api/appearance"
    ),
  saveAppearance: (value: { theme: string; overrides: Record<string, unknown> }) =>
    request<{ theme: string; overrides: Record<string, unknown> }>("/api/appearance", { method: "PUT", body: value }),
  /** The organisation's default look and whether it is locked; set by an administrator. */
  orgAppearance: () =>
    request<{ default: { theme: string; overrides: Record<string, unknown> } | null; locked: boolean }>("/api/appearance/org"),
  saveOrgAppearance: (value: { default: { theme: string; overrides: Record<string, unknown> } | null; locked: boolean }) =>
    request<{ default: { theme: string; overrides: Record<string, unknown> } | null; locked: boolean }>("/api/appearance/org", {
      method: "PUT",
      body: value,
    }),

  /** What each reply shows beneath it — one decision for the whole site. `canEdit` is true for the site's administrator. */
  replyDisplay: () =>
    request<{ verified: boolean; provenance: boolean; assumptions: boolean; model: boolean; editReply: boolean; canEdit: boolean }>("/api/reply-display"),
  saveReplyDisplay: (value: { verified: boolean; provenance: boolean; assumptions: boolean; model: boolean; editReply: boolean }) =>
    request<{ verified: boolean; provenance: boolean; assumptions: boolean; model: boolean; editReply: boolean; canEdit: boolean }>("/api/reply-display", {
      method: "PUT",
      body: value,
    }),

  /** Which modules the left rail lists — one decision for the whole site. `canEdit` is true for the site's administrator. */
  sidebarModules: () => request<{ hidden: string[]; canEdit: boolean }>("/api/sidebar-modules"),
  saveSidebarModules: (value: { hidden: string[] }) =>
    request<{ hidden: string[]; canEdit: boolean }>("/api/sidebar-modules", { method: "PUT", body: value }),

  /** A question about the list or the open record, answered by the model from what the panel sends. */
  aiAnswer: (doctype: string, body: { question: string; filters?: unknown; rows?: Record<string, unknown>[]; total?: number | null; record?: Record<string, unknown> | null }) =>
    request<{ answer: string; filters: [string, string, unknown][]; order_by?: string; partial: boolean; dropped: string[] }>(
      `/api/list-views/${encodeURIComponent(doctype)}/ai-answer`,
      { method: "POST", body, timeoutMs: 60_000 }
    ),

  /** A filter said in words, as the list tool's own filters — checked against the doctype's fields. */
  aiFilter: (doctype: string, prompt: string) =>
    request<{
      filters: [string, string, unknown][];
      or_filters: [string, string, unknown][];
      order_by?: string;
      explanation: string;
      dropped: string[];
    }>(`/api/list-views/${encodeURIComponent(doctype)}/ai-filter`, { method: "POST", body: { prompt }, timeoutMs: 60_000 }),

  /**
   * Run a tool from the panel. `conversationId` says which thread the result
   * is filed under — the one on screen — so a lookup made in one window never
   * turns up in the results of another.
   */
  executeTool: (name: string, args: unknown, conversationId?: string | null) =>
    request<Record<string, unknown>>("/api/tools/execute", {
      method: "POST",
      body: { name, arguments: args, ...(conversationId ? { conversationId } : {}) },
    }).then(watchForExpiredErp),

  /**
   * A tool call whose result is going straight back to the Realtime model.
   * The server returns the trimmed view rather than the whole payload — a
   * whole-team timesheet is a quarter of a million characters, and slicing
   * that here used to cut it off long before the part that answered the
   * question. The results panel still gets the full copy over the live link.
   */
  executeToolForModel: (name: string, args: unknown) =>
    request<{ output: string; error?: string }>("/api/tools/execute", {
      method: "POST",
      body: { name, arguments: args, for_model: true },
    }),

  /**
   * Answer the write Jarvis is holding — what the Confirm and Cancel buttons
   * post. No model turn is involved: the server already has the exact proposal
   * that was read back, so the click runs that and nothing else.
   */
  resolvePending: (decision: "confirm" | "cancel", conversationId?: string | null, proposalId?: string) =>
    request<{
      ok: boolean;
      decision: "confirm" | "cancel";
      label: string;
      result: Record<string, unknown>;
      conversation_id: string;
    }>("/api/chat/pending", {
      method: "POST",
      body: { decision, proposalId, ...(conversationId ? { conversationId } : {}) },
    }),

  /**
   * A lookup the UI runs for its own rendering — the record drawer fetching the
   * document behind a clicked row. Same dispatcher, but the outcome never
   * becomes an artifact, so opening ten rows doesn't bury the list they came
   * from under ten single-record results.
   */
  /** What this document can be turned into — ERPNext's own Create menu. */
  /**
   * `states` are the doc_states visible in the caller's own result. They let
   * the server answer "is this submittable" from evidence when a site will
   * not let the operator read DocType — without them, such a site silently
   * loses its Submit button.
   */
  docActions: (doctype: string, docstatus?: number, states: string[] = []) => {
    const query = new URLSearchParams();
    if (docstatus !== undefined) query.set("docstatus", String(docstatus));
    if (states.length) query.set("states", states.join(","));
    const suffix = query.toString();
    return request<{
      doctype: string;
      submittable: boolean;
      submittable_known: boolean;
      actions: { id: string; label: string; target: string }[];
      lifecycle: { id: string; label: string; irreversible: boolean; note: string; field?: string; value?: string; thenSubmit?: boolean }[];
      /** An ERPNext workflow governs this doctype, so the lifecycle list is empty by design. */
      workflow_governed?: boolean;
    }>(`/api/doc-actions/${encodeURIComponent(doctype)}${suffix ? `?${suffix}` : ""}`);
  },

  /**
   * What a SELECTION can do — the intersection across its rows, so a mixed
   * selection offers nothing rather than a button that half-works.
   */
  bulkActionsFor: (doctype: string, docstatuses: number[], states: string[] = []) => {
    const query = new URLSearchParams({ docstatuses: docstatuses.join(",") });
    if (states.length) query.set("states", states.join(","));
    return request<{
      doctype: string;
      lifecycle: { id: string; label: string; irreversible: boolean; note: string; field?: string; value?: string; thenSubmit?: boolean }[];
      /** An ERPNext workflow governs this doctype, so the lifecycle list is empty by design. */
      workflow_governed?: boolean;
    }>(`/api/doc-actions/${encodeURIComponent(doctype)}?${query.toString()}`);
  },

  /** Queue one lifecycle act across a selection. Answers once queued, not once done. */
  runBulkAction: (doctype: string, action: string, names: string[], sourceResultId?: string) =>
    request<{ queued: boolean; jobId?: string; count?: number; message?: string }>(
      "/api/bulk-actions",
      { method: "POST", body: { doctype, action, names, sourceResultId } }
    ),
  /** How a queued batch is getting on, and its report once done. */
  /**
   * Everything waiting on the signed-in operator, across every doctype. The
   * fan-out happens on the server; grouping happens on the screen, so
   * switching dimension costs nothing.
   */
  approvals: (page: ApprovalPage = {}) => {
    // Offset and limit travel only with a doctype, because "skip 100" has no
    // meaning across a fan-out whose lanes are read in parallel. Sending them
    // without one would be a query the server has to ignore, and a caller that
    // believes it paged when it did not.
    const query = new URLSearchParams();
    if (page.doctype) {
      query.set("doctype", page.doctype);
      if (page.offset !== undefined) query.set("offset", String(page.offset));
      if (page.limit !== undefined) query.set("limit", String(page.limit));
    }
    if (page.fresh) query.set("fresh", "1");
    if (page.deferActions) query.set("actions", "0");
    const suffix = query.toString();
    return request<ApprovalQueue>(`/api/approvals${suffix ? `?${suffix}` : ""}`);
  },

  /** The console's piles: headings, counts, totals and facets, without the rows. */
  approvalsView: (body: { view: ApprovalViewState; reload?: boolean; fresh?: boolean; selected?: string[] }) =>
    request<ApprovalView>("/api/approvals/view", { method: "POST", body, timeoutMs: 120_000 }),

  /** One page of one facet's options — the rail's people or document types, twenty at a time. */
  approvalsFacet: (body: { view: ApprovalViewState; facet: string; offset: number; limit: number; search?: string }) =>
    request<{ facet: string; total: number; all: number; offset: number; limit: number; options: { id: string; label: string; count: number }[] }>("/api/approvals/facet", { method: "POST", body, timeoutMs: 120_000 }),

  /** One page of one pile's rows. */
  approvalsRows: (body: { view: ApprovalViewState; pile: string; offset: number; limit: number }) =>
    request<{ version: number; items: ApprovalItem[]; count: number; owed: string[]; errors: string[]; pile: ApprovalPileSummary | null; missing?: boolean }>("/api/approvals/view/rows", { method: "POST", body, timeoutMs: 120_000 }),

  /** Every row under a pile's heading, buttons read — what Select ticks and Approve all decides. */
  approvalsMembers: (body: { view: ApprovalViewState; pile: string }) =>
    request<{ version: number; items: ApprovalItem[]; owed: string[]; errors: string[]; pile: ApprovalPileSummary | null; missing?: boolean }>("/api/approvals/view/members", { method: "POST", body, timeoutMs: 120_000 }),

  /** The site's next page of one doctype, added to the held queue. */
  approvalsMore: (doctype: string) =>
    request<{ version: number }>("/api/approvals/view/more", { method: "POST", body: { doctype }, timeoutMs: 120_000 }),

  /** The buttons for workflow rows the queue was read without: what the site lets this person do to each. */
  approvalTransitions: (doctype: string, rows: { name: string; state: string | null }[], fresh = false) =>
    request<{
      error: string | null;
      rows: { name: string; actions: ApprovalItem["actions"]; blocked: string | null }[];
    }>("/api/approvals/transitions", { method: "POST", body: { doctype, rows, ...(fresh ? { fresh: true } : {}) } }),

  /**
   * Three questions worth asking in a module, from the model, cached on the
   * server per operator and module for the day. Asked for by a click, never
   * on arrival: it is the one part of the landing page that costs a model call.
   */
  /** Revise a builder draft by saying what should change; the answer is the whole draft again, unsaved. */
  reviseBuilder: (kind: "report" | "chart" | "card" | "dashboard", draft: Record<string, unknown>, instruction: string, history: { instruction: string; note: string }[] = []) =>
    request<{ revised: boolean; draft: Record<string, unknown>; title?: string; note: string; question?: string }>("/api/builders/revise", {
      method: "POST",
      body: { kind, draft, instruction, history },
      timeoutMs: 60_000,
    }),

  suggestDocuments: (module: string, doctypes: string[], queries: string[]) =>
    request<{ questions: { question: string; doctype: string | null }[]; cached: boolean }>("/api/documents/suggest", {
      method: "POST",
      body: { module, doctypes, queries },
      timeoutMs: 30_000,
    }),

  /**
   * The report tiles Genie keeps beside a site dashboard — the site's own
   * Dashboard has no field for them. Keyed by the dashboard's name, shared
   * by everyone who can open it.
   */
  dashboardExtras: (name: string) => request<{ reports: ReportTile[] }>(`/api/dashboards/${encodeURIComponent(name)}/extras`),

  saveDashboardExtras: (name: string, extras: { reports: ReportTile[] }) =>
    request<{ reports: ReportTile[] }>(`/api/dashboards/${encodeURIComponent(name)}/extras`, { method: "PUT", body: extras }),

  bulkStatus: (jobId: string) =>
    request<{
      state: "queued" | "running" | "done" | "failed" | "dead";
      error: string | null;
      report: { label: string; isError: boolean; summary?: string; succeeded?: string[]; refused?: { name: string; reason: string }[] } | null;
    }>(`/api/bulk-actions/${encodeURIComponent(jobId)}`),

  /**
   * Run a result's own query again, replacing it in place.
   *
   * Only reads are repeated — the server refuses anything else, so this can
   * be offered beside a write's outcome without the button being a second
   * write.
   */
  refreshResult: (id: string) =>
    request<{ refreshed: true; id: string }>(
      `/api/results/${encodeURIComponent(id)}/refresh`,
      { method: "POST" }
    ),

  executeToolQuiet: (name: string, args: unknown) =>
    request<Record<string, unknown>>("/api/tools/execute", {
      method: "POST",
      body: { name, arguments: args, silent: true },
    }).then(watchForExpiredErp),

  streamTranscript: (itemId: string, role: "user" | "assistant", text: string) =>
    request<{ ok: true }>("/api/transcript/stream", {
      method: "POST",
      body: { itemId, role, text },
    }),

  finalizeTranscript: (
    itemId: string,
    role: "user" | "assistant",
    text: string,
    interrupted = false,
    conversationId?: string | null
  ) =>
    request<{ ok: true }>("/api/transcript/finalize", {
      method: "POST",
      body: { itemId, role, text, interrupted, ...(conversationId ? { conversationId } : {}) },
    }),

  logTimeline: (kind: string, label: string, detail?: string) =>
    request<{ ok: true }>("/api/timeline", { method: "POST", body: { kind, label, detail } }),

  setVoiceState: (orbState: string, sessionActive: boolean) =>
    request<{ ok: true }>("/api/voice-state", {
      method: "POST",
      body: { orbState, sessionActive },
    }),

  realtimeToken: () =>
    request<{ value?: string; client_secret?: { value: string }; model: string }>(
      "/api/realtime/token",
      { method: "POST" }
    ),

  /**
   * Typed conversation — no Realtime session, no audio tokens.
   * mode "browser" marks an '@'-prefixed message: fulfil on screen, in a
   * visual-browser tab, rather than answering with prose.
   *
   * `opts.signal` is the Stop button: abort it and the server sees the socket
   * close, stops the turn between rounds and finalizes what the assistant had
   * said as an interrupted message. The rejection is a RequestAborted, not an
   * ApiError — see isAborted().
   *
   * `opts.references` are the records pinned in the composer. They ride beside
   * the message rather than inside it so the operator's own bubble keeps the
   * sentence they actually typed.
   */
  chat: (
    message: string,
    mode?: "chat" | "browser" | "current_tab",
    tabId?: string,
    opts: {
      signal?: AbortSignal;
      references?: readonly ChatReference[];
      /** The thread this message belongs to. Always sent by the Genie shell;
       *  the server falls back to the most recent thread only when omitted. */
      conversationId?: string | null;
      /** The report, chart, card or dashboard open beside the chat, if any. */
      screen?: import("./builderTypes").ScreenContext | null;
      /** Temporary chat: answered as usual, nothing learned from it. */
      temporary?: boolean;
      /** Asked from the Ask again list: last time's reads are replayed before the model writes the reply. */
      recallId?: string | null;
      /** Memory items set aside for this turn — "not this time" on an assumption chip. */
      withoutMemory?: string[] | null;
    } = {}
  ) =>
    // Two shapes come back. The usual one is the synchronous ChatOutcome; an
    // async deployment (GENIE_SYNC_CHAT=0) answers HTTP 202 with ChatQueued
    // instead — same promise, and 202 is `response.ok`, so it resolves rather
    // than throws. Callers discriminate with isChatQueued() and must not treat
    // the queued form as an error or render a reply from it: the transcript
    // arrives over the live socket either way.
    request<ChatOutcome | ChatQueued>("/api/chat", {
      method: "POST",
      // tabId rides along for "@@": the panel knows which tab the user is
      // looking at, and that is the tab the request must land in.
      body: {
        message,
        ...(mode ? { mode, ...(tabId ? { tabId } : {}) } : {}),
        ...(opts.conversationId ? { conversationId: opts.conversationId } : {}),
        ...(opts.references?.length ? { references: opts.references.map(onTheWire) } : {}),
        ...(opts.screen ? { screen: opts.screen } : {}),
        ...(opts.temporary ? { temporary: true } : {}),
        ...(opts.recallId ? { recallId: opts.recallId } : {}),
        ...(opts.withoutMemory?.length ? { withoutMemory: opts.withoutMemory } : {}),
      },
      timeoutMs: 300_000,
      signal: opts.signal,
    }),

  /**
   * Correct an earlier request and run it again. The server archives the old
   * wording, supersedes whatever that request had in flight, and tells the
   * model what the previous attempts already wrote — so a corrected sentence
   * never quietly creates a second document.
   */
  editMessage: (messageId: string, message: string) =>
    request<{
      reply: string;
      message_id: string;
      message_version: number;
      superseded_actions: string[];
      existing_document?: string;
    }>(`/api/chat/messages/${encodeURIComponent(messageId)}/edit`, {
      method: "POST",
      body: { message },
      timeoutMs: 300_000,
    }),

  /**
   * Correct a reply's wording in place — no re-run, no model call. The old
   * wording is archived the same way an edited request's is.
   */
  editReply: (messageId: string, text: string) =>
    request<{ message_id: string; text: string; message_version: number }>(
      `/api/chat/messages/${encodeURIComponent(messageId)}`,
      { method: "PATCH", body: { text } }
    ),

  /** Run an earlier request again, unedited — the Retry button on an attempt. */
  retryAction: (actionId: string) =>
    request<{
      reply: string;
      message_id: string;
      message_version: number;
      superseded_actions: string[];
      existing_document?: string;
    }>(`/api/chat/actions/${encodeURIComponent(actionId)}/retry`, {
      method: "POST",
      timeoutMs: 300_000,
    }),

  /* -- conversations: several independent threads per operator ---------- */

  conversations: (filter: ConversationFilter = {}, limit?: number, offset?: number) =>
    request<{ conversations: Conversation[]; current: string | null; hasMore?: boolean }>(
      `/api/conversations${filterQuery(filter, limit, offset)}`
    ),

  /** One thread by id — a 404 when it is not this operator's, or is gone. */
  conversation: (id: string) =>
    request<{ conversation: Conversation; messages: Message[] }>(`/api/conversations/${encodeURIComponent(id)}`),

  /* ---- recall: questions asked before, suggested as you type ---- */

  recallSuggest: (q: string, limit = 8) =>
    request<{ suggestions: RecallQuery[] }>(`/api/recall/suggest?q=${encodeURIComponent(q)}&limit=${limit}`),
  recallTags: () => request<{ tags: { tag: string; count: number }[] }>("/api/recall/tags"),
  /** Which areas each chat thread touches, from the curated questions asked in it. */
  recallThreads: () => request<{ areas: Record<string, string[]> }>("/api/recall/threads"),
  recallList: (options: { q?: string; tag?: string; limit?: number; offset?: number; order?: "often" | "recent"; scope?: "mine" | "team" | "all" } = {}) => {
    const p = new URLSearchParams();
    if (options.scope) p.set("scope", options.scope);
    if (options.q) p.set("q", options.q);
    if (options.tag) p.set("tag", options.tag);
    if (options.limit) p.set("limit", String(options.limit));
    if (options.offset) p.set("offset", String(options.offset));
    if (options.order) p.set("order", options.order);
    const qs = p.toString();
    return request<{ items: RecallQuery[]; hasMore: boolean }>(`/api/recall${qs ? `?${qs}` : ""}`);
  },
  recallStatus: () => request<RecallSyncStatus>("/api/recall/status"),
  /** Replies from the last week whose "since last time" showed a real move. */
  recallNudges: () => request<{ nudges: RecallNudge[] }>("/api/recall/nudges"),
  /** Two lines of where a thread stood, for the top of a reopened one. */
  conversationRecap: (id: string) => request<ThreadRecap>(`/api/conversations/${encodeURIComponent(id)}/recap`),
  /** Admin: share a question with everyone on the site, or take it back. */
  recallShare: (id: string) => request<{ id: string }>(`/api/recall/${encodeURIComponent(id)}/share`, { method: "POST", body: "{}" }),
  recallUnshare: (id: string) => request<{ ok: boolean }>(`/api/recall/${encodeURIComponent(id)}/share`, { method: "DELETE" }),
  recallSync: (since?: number) =>
    request<{
      scanned: number;
      kept: number;
      added: number;
      updated: number;
      watermark: number;
      review: { reviewed: number; kept: number; dropped: number; promptsAdded: number; merged: number; retired: number; note: string | null };
      status: RecallSyncStatus;
    }>(
      "/api/recall/sync",
      { method: "POST", body: JSON.stringify(since === undefined ? {} : { since }) }
    ),
  recallHide: (id: string) => request<{ ok: true }>(`/api/recall/${encodeURIComponent(id)}`, { method: "DELETE" }),
  /** Make a question standing: asked on a schedule in its own thread, the answer delivered. */
  recallStanding: (id: string, options: { recurrence?: string; time?: string; timezone?: string; channel?: string } = {}) =>
    request<{ automationId: string; recurrence: string; time: string }>(`/api/recall/${encodeURIComponent(id)}/standing`, {
      method: "POST",
      body: JSON.stringify(options),
    }),

  /** Every tag in use with counts — the filter bar. */
  conversationTags: () =>
    request<{ tags: { tag: string; count: number }[] }>("/api/conversations/tags"),

  tagConversation: (id: string, tags: string[]) =>
    request<Conversation>(`/api/conversations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { tags },
    }),

  createConversation: (title?: string) =>
    request<Conversation>("/api/conversations", {
      method: "POST",
      body: title ? { title } : {},
    }),

  renameConversation: (id: string, title: string) =>
    request<Conversation>(`/api/conversations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { title },
    }),

  deleteConversation: (id: string) =>
    request<{ ok: true; current: string | null }>(
      `/api/conversations/${encodeURIComponent(id)}`,
      { method: "DELETE" }
    ),

  /** The causal chain of a conversation's work: turns, proposals, writes and
   *  the ERP documents they left behind. */
  conversationTrace: (id: string) =>
    request<ConversationTrace>(`/api/conversations/${encodeURIComponent(id)}/trace`),

  /** Every wording a request has had, and every ERP attempt it caused. */
  messageHistory: (messageId: string) =>
    request<{
      message: { id: string; version: number; editedAt?: number };
      versions: MessageVersion[];
      attempts: ErpAction[];
    }>(`/api/chat/messages/${encodeURIComponent(messageId)}/history`),

  voiceTools: () =>
    request<{
      count: number;
      total_available: number;
      tools: { name: string; description: string; write: boolean }[];
    }>("/api/realtime/tools"),

  addTodo: (title: string, priority?: string) =>
    request<{ id: string }>("/api/todos", { method: "POST", body: { title, priority } }),

  toggleTodo: (title: string, done: boolean) =>
    request<{ ok: boolean }>("/api/todos/toggle", { method: "POST", body: { title, done } }),

  deleteTodo: (title: string) =>
    request<{ ok: boolean }>("/api/todos", { method: "DELETE", body: { title } }),

  forgetFact: (key: string) =>
    request<{ forgotten: boolean }>("/api/memory", { method: "DELETE", body: { key } }),

  searchConversations: (q: string, filter: ConversationFilter = {}, offset?: number) =>
    request<{
      results: (Conversation & {
        snippet?: string;
        matchedAt?: number;
        matchedMessageId?: string;
      })[];
      hasMore?: boolean;
    }>(
      `/api/conversations/search?q=${encodeURIComponent(q)}${filterQuery(filter, undefined, offset).replace(/^\?/, "&")}`
    ),

  knowledge: () => request<{ items: KnowledgeItem[] }>("/api/knowledge"),
  /** Write knowledge by hand: into my own memory, or (as an administrator) the company's. */
  saveKnowledge: (input: { scope: "org" | "user"; kind: string; title: string; body: string }) =>
    request<{ id: string; status: KnowledgeItem["status"]; updated: boolean; conflictId?: string }>("/api/knowledge", { method: "POST", body: input }),

  /* -------- the learning and memory system -------- */

  /** What Genie remembers about me, with the switch, pending check-ins and my corrections. */
  memory: () =>
    request<{
      items: KnowledgeItem[];
      settings: LearningSettings;
      suggestions: LearningSuggestion[];
      conflicts: KnowledgeConflict[];
    }>("/api/memory"),
  updateMemory: (id: string, patch: { title?: string; body?: string; reason?: string }) =>
    request<KnowledgeItem>(`/api/memory/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
  forgetMemory: (id: string) =>
    request<{ ok: boolean }>(`/api/memory/${encodeURIComponent(id)}`, { method: "DELETE" }),
  setMemoryDisabled: (id: string, disabled: boolean) =>
    request<{ ok: boolean }>(`/api/memory/${encodeURIComponent(id)}/${disabled ? "disable" : "enable"}`, { method: "POST" }),
  knowledgeVersions: (id: string) =>
    request<{ versions: KnowledgeVersion[] }>(`/api/knowledge/${encodeURIComponent(id)}/versions`),
  updateKnowledge: (id: string, patch: { title?: string; body?: string; reason?: string }) =>
    request<KnowledgeItem>(`/api/knowledge/${encodeURIComponent(id)}`, { method: "PATCH", body: patch }),
  learningSettings: () => request<LearningSettings>("/api/learning/settings"),
  setLearningEnabled: (learningEnabled: boolean) =>
    request<LearningSettings>("/api/learning/settings", { method: "PUT", body: { learningEnabled } }),
  learningSuggestions: () => request<{ suggestions: LearningSuggestion[] }>("/api/learning/suggestions"),
  acceptSuggestion: (id: string) =>
    request<{ itemId: string }>(`/api/learning/suggestions/${encodeURIComponent(id)}/accept`, { method: "POST" }),
  dismissSuggestion: (id: string) =>
    request<{ ok: boolean }>(`/api/learning/suggestions/${encodeURIComponent(id)}/dismiss`, { method: "POST" }),
  knowledgeConflicts: () => request<{ conflicts: KnowledgeConflict[] }>("/api/admin/knowledge/conflicts"),
  decideConflict: (id: string, decision: "accept" | "dismiss", note?: string) =>
    request<{ ok: boolean }>(`/api/admin/knowledge/conflicts/${encodeURIComponent(id)}/${decision}`, { method: "POST", body: { note } }),
  messageFeedback: (id: string, value: FeedbackValue, note?: string) =>
    request<{ ok: boolean; adjusted: number }>(`/api/chat/messages/${encodeURIComponent(id)}/feedback`, { method: "POST", body: { value, note } }),
  messageInfluences: (id: string) =>
    request<{ influences: Influence[]; verification: Verification | null; feedback: FeedbackValue | null }>(
      `/api/chat/messages/${encodeURIComponent(id)}/influences`
    ),
  feedbackFor: (ids: string[]) =>
    request<{ feedback: Record<string, FeedbackValue> }>(`/api/chat/feedback?ids=${encodeURIComponent(ids.join(","))}`),
  evaluateSkill: (slug: string, scope: "org" | "user") =>
    request<{ skill: SkillMeta & { evaluation?: SkillEvaluation }; evaluation: SkillEvaluation }>(
      `/api/skills/${encodeURIComponent(slug)}/evaluate`, { method: "POST", body: { scope } }
    ),
  transitionSkill: (slug: string, scope: "org" | "user", verb: "approve" | "activate" | "retire", note?: string) =>
    request<SkillMeta>(`/api/skills/${encodeURIComponent(slug)}/${verb}`, { method: "POST", body: { scope, note } }),
  rollbackSkill: (slug: string, scope: "org" | "user", version: number) =>
    request<SkillMeta>(`/api/skills/${encodeURIComponent(slug)}/rollback`, { method: "POST", body: { scope, version } }),
  learningReports: (days = 30) => request<LearningReport>(`/api/admin/learning/reports?days=${days}`),
  runLearningHousekeeping: () => request<Record<string, number>>("/api/admin/learning/housekeeping", { method: "POST", timeoutMs: 120_000 }),
  proposeSkills: () => request<{ created: number }>("/api/admin/learning/propose-skills", { method: "POST", timeoutMs: 120_000 }),

  intake: () =>
    request<{
      documents: IntakeDocument[];
      counts: { pending: number; failed: number };
      enabled: boolean;
    }>("/api/intake"),

  claimIntake: (id: string) =>
    request<{ ok: boolean }>(`/api/intake/${encodeURIComponent(id)}/claim`, { method: "POST" }),

  intakeFiled: (id: string, doctype: string, name: string) =>
    request<{ ok: boolean }>(`/api/intake/${encodeURIComponent(id)}/filed`, {
      method: "POST",
      body: { doctype, name },
    }),

  dismissIntake: (id: string, reason?: string) =>
    request<{ ok: boolean }>(`/api/intake/${encodeURIComponent(id)}/dismiss`, {
      method: "POST",
      body: { reason },
    }),

  sweepIntake: () =>
    request<{ seen: number; ingested: number; skipped: number; failed: number }>(
      "/api/admin/intake/sweep",
      { method: "POST", timeoutMs: 300_000 }
    ),

  /* ---------------------------------------------------------------- */
  /* The mailbox                                                       */
  /* ---------------------------------------------------------------- */

  inbox: (opts: { folder?: string; files?: "all" | "images" | "documents"; q?: string; limit?: number; offset?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.folder) params.set("folder", opts.folder);
    if (opts.files && opts.files !== "all") params.set("files", opts.files);
    if (opts.q) params.set("q", opts.q);
    if (opts.limit) params.set("limit", String(opts.limit));
    if (opts.offset) params.set("offset", String(opts.offset));
    const query = params.toString();
    return request<{
      messages: InboxMessage[];
      /** Messages in the whole folder, across every page. */
      total: number;
      offset: number;
      limit: number;
      counts: InboxCounts;
      folder: InboxFolder;
      enabled: boolean;
      canWrite: boolean;
      draftable: string[];
      lastSyncAt: number | null;
      lastError: string | null;
    }>(`/api/inbox${query ? `?${query}` : ""}`);
  },

  syncInbox: () =>
    request<{ seen: number; added: number; failed: number; counts: InboxCounts }>(
      "/api/inbox/sync",
      { method: "POST", timeoutMs: 300_000 }
    ),

  inboxMessage: (id: string) =>
    request<InboxFullMessage>(`/api/inbox/${encodeURIComponent(id)}`),

  inboxRaw: (id: string) =>
    request<{
      live: boolean;
      note?: string;
      html: string;
      text: string;
      doc: Record<string, unknown> | null;
    }>(`/api/inbox/${encodeURIComponent(id)}/raw`),

  markInbox: (id: string, patch: { seen?: boolean; flagged?: boolean; archived?: boolean }) =>
    request<InboxMessage>(`/api/inbox/${encodeURIComponent(id)}/mark`, {
      method: "POST",
      body: patch,
    }),

  analyseInbox: (id: string) =>
    request<InboxAnalysis>(`/api/inbox/${encodeURIComponent(id)}/analyse`, {
      method: "POST",
      timeoutMs: 180_000,
    }),

  composeInboxReply: (id: string, instruction?: string) =>
    request<{ body: string }>(`/api/inbox/${encodeURIComponent(id)}/compose`, {
      method: "POST",
      body: { instruction },
      timeoutMs: 180_000,
    }),

  extractInboxAttachment: (id: string, fileUrl?: string) =>
    request<{
      attachment: InboxAttachment;
      doctype: string | null;
      values: Record<string, unknown>;
      confidence: number;
      missing: string[];
      summary: string;
    }>(`/api/inbox/${encodeURIComponent(id)}/extract`, {
      method: "POST",
      body: { fileUrl },
      timeoutMs: 300_000,
    }),

  replyToInbox: (
    id: string,
    body: { body: string; cc?: string; subject?: string; replyAll?: boolean }
  ) =>
    request<{ sent: true; recipients: string; cc?: string }>(
      `/api/inbox/${encodeURIComponent(id)}/reply`,
      { method: "POST", body, timeoutMs: 120_000 }
    ),

  draftFromInbox: (id: string, doctype: string, doc: Record<string, unknown>) =>
    request<{ doctype: string; name: string; linked: boolean }>(
      `/api/inbox/${encodeURIComponent(id)}/draft`,
      { method: "POST", body: { doctype, doc }, timeoutMs: 120_000 }
    ),

  linkInbox: (id: string, doctype: string, name: string) =>
    request<{ doctype: string; name: string }>(`/api/inbox/${encodeURIComponent(id)}/link`, {
      method: "POST",
      body: { doctype, name },
    }),

  skills: (opts: { page?: number; pageSize?: number; q?: string; module?: string; scope?: string } = {}) => {
    const params = new URLSearchParams();
    if (opts.page) params.set("page", String(opts.page));
    if (opts.pageSize) params.set("pageSize", String(opts.pageSize));
    if (opts.q) params.set("q", opts.q);
    if (opts.module) params.set("module", opts.module);
    if (opts.scope) params.set("scope", opts.scope);
    const query = params.toString();
    return request<SkillPage>(`/api/skills${query ? `?${query}` : ""}`);
  },

  draftSkill: (ask: string) =>
    request<SkillDraft>("/api/skills/draft", {
      method: "POST",
      body: { request: ask },
      timeoutMs: 120_000,
    }),

  skill: (slug: string) => request<SkillDetail>(`/api/skills/${encodeURIComponent(slug)}`),

  /** Redraft a playbook in use from its recorded failures: a draft beside it, evaluated. */
  redraftSkill: (slug: string) =>
    request<SkillRedraft>(`/api/skills/${encodeURIComponent(slug)}/redraft`, { method: "POST", timeoutMs: 180_000 }),

  /** A playbook's test questions, replaced; `scope` names the version (a redraft shares its original's). */
  saveSkillTests: (slug: string, scope: "global" | "org" | "user", questions: string[]) =>
    request<{ questions: string[] }>(`/api/skills/${encodeURIComponent(slug)}/tests`, { method: "PUT", body: { scope, questions } }),

  /** Questions people actually asked when the playbook ran. */
  skillTestSuggestions: (slug: string, scope: "global" | "org" | "user") =>
    request<{ questions: string[] }>(`/api/skills/${encodeURIComponent(slug)}/tests/suggestions?scope=${scope}`),

  /** Runs every test question against the site — a few model rounds each, one after another. */
  runSkillTests: (slug: string, scope: "global" | "org" | "user") =>
    request<{ run: SkillTestRun }>(`/api/skills/${encodeURIComponent(slug)}/tests/run`, { method: "POST", body: { scope }, timeoutMs: 900_000 }),

  /** Replies that followed a playbook against those that did not. */
  playbookImpact: (days = 30) => request<PlaybookImpact>(`/api/admin/learning/playbook-impact?days=${days}`),

  /** A playbook's runs, newest first; `before` pages back. */
  skillRuns: (slug: string, scope: "global" | "org" | "user", before?: number) =>
    request<{ runs: PlaybookRun[]; more: boolean }>(`/api/skills/${encodeURIComponent(slug)}/runs?scope=${scope}${before ? `&before=${before}` : ""}`),

  saveSkill: (
    slug: string,
    payload: {
      scope: "org" | "user";
      title: string;
      description: string;
      keywords?: string[];
      body: string;
      changeNote?: string;
    }
  ) =>
    request<{ id: string; version: number }>(`/api/skills/${encodeURIComponent(slug)}`, {
      method: "PUT",
      body: payload,
    }),

  deleteSkill: (slug: string, scope: "org" | "user") =>
    request<{ ok: boolean }>(`/api/skills/${encodeURIComponent(slug)}?scope=${scope}`, {
      method: "DELETE",
    }),

  seedErpKnowledge: () =>
    request<{ seeded: number; skipped: string[] }>("/api/admin/knowledge/seed-erp", {
      method: "POST",
      timeoutMs: 120_000,
    }),

  deleteKnowledge: (id: string) =>
    request<{ ok: boolean }>(`/api/knowledge/${encodeURIComponent(id)}`, { method: "DELETE" }),

  knowledgeProposals: () =>
    request<{ proposals: KnowledgeItem[] }>("/api/admin/knowledge/proposals"),

  approveKnowledge: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/knowledge/${encodeURIComponent(id)}/approve`, {
      method: "POST",
    }),

  rejectKnowledge: (id: string) =>
    request<{ ok: boolean }>(`/api/admin/knowledge/${encodeURIComponent(id)}/reject`, {
      method: "POST",
    }),

  runLearningPass: () =>
    request<{ users: number; saved: number; proposed: number }>("/api/admin/learning/run", {
      method: "POST",
      timeoutMs: 300_000,
    }),

  reconnectMcp: (serverId: string) =>
    request<{ ok: boolean }>("/api/mcp/reconnect", { method: "POST", body: { serverId } }),

  /**
   * Voice dictation: one recorded take in, text out. Raw audio body rather
   * than JSON, so this bypasses request(); same bearer token, same error
   * shape. The server relays to the configured STT provider — audio is never
   * persisted. Partial (in-progress) calls return the verbatim transcript for
   * live feedback; the final call is translated to English server-side unless
   * that is disabled.
   */
  transcribe: async (
    audio: Blob,
    opts: { partial?: boolean; retried?: boolean } = {}
  ): Promise<{ text: string }> => {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": audio.type || "audio/webm",
    };
    if (token.value) headers.Authorization = `Bearer ${token.value}`;
    const response = await fetch(
      apiUrl(`/api/dictation/transcribe${opts.partial ? "?partial=1" : ""}`),
      {
        method: "POST",
        headers,
        body: audio,
        signal: AbortSignal.timeout(60_000),
      }
    );
    const text = await response.text();
    const payload = parseJsonBody(text, response);
    if (!response.ok) {
      // Same expired-access-token handling as request(): renew once and
      // replay, so a token expiring mid-dictation doesn't eat the take.
      if (response.status === 401 && !opts.retried) {
        if (await refreshSession()) return api.transcribe(audio, { ...opts, retried: true });
        dropSession();
      } else if (response.status === 401) {
        dropSession();
      }
      throw new ApiError(
        payload?.message ?? `Transcription failed (${response.status})`,
        response.status,
        payload?.error
      );
    }
    return payload as { text: string };
  },

  /* -- the agent builder (administrators) ------------------------------ */

  agentBlueprints: () =>
    request<{ blueprints: BlueprintSummary[]; palette: BuilderPalette; model: { ok: boolean; reason?: string }; outbound: OutboundPolicy; secrets: SecretInfo[] }>("/api/admin/agent-builder"),
  saveAgentOutbound: (policy: OutboundPolicy) => request<OutboundPolicy>("/api/admin/agent-builder/outbound", { method: "PUT", body: policy }),
  setAgentSecret: (name: string, value: string) =>
    request<{ secrets: SecretInfo[] }>(`/api/admin/agent-builder/secrets/${encodeURIComponent(name)}`, { method: "PUT", body: { value } }),
  deleteAgentSecret: (name: string) => request<{ secrets: SecretInfo[] }>(`/api/admin/agent-builder/secrets/${encodeURIComponent(name)}`, { method: "DELETE" }),
  agentBlueprint: (id: string) =>
    request<{ blueprint: Blueprint; problems: GraphProblem[]; installs: number }>(`/api/admin/agent-builder/${encodeURIComponent(id)}`),
  createAgentBlueprint: (meta: BlueprintMeta, draft?: BuilderGraph) =>
    request<{ blueprint: Blueprint; problems: GraphProblem[] }>("/api/admin/agent-builder", { method: "POST", body: { meta, ...(draft ? { draft } : {}) } }),
  saveAgentBlueprint: (id: string, input: { meta?: BlueprintMeta; draft?: BuilderGraph }) =>
    request<{ blueprint: Blueprint; problems: GraphProblem[] }>(`/api/admin/agent-builder/${encodeURIComponent(id)}`, { method: "PUT", body: input }),
  rehearseAgentBlueprint: (id: string, focus?: { doctype: string; name: string }) =>
    request<{ run: AgentRun; proposals: AgentProposal[]; notes: AgentNote[]; refusals: { rule: string; message: string }[] }>(
      `/api/admin/agent-builder/${encodeURIComponent(id)}/rehearse`,
      { method: "POST", body: focus ? { focus } : {}, timeoutMs: 300_000 }
    ),
  /** The site's doctypes, for the builder's pickers. */
  builderDoctypes: (q = "") =>
    request<{ doctypes: { name: string; module: string | null; custom: boolean }[] }>(`/api/admin/agent-builder/site/doctypes${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  /** One doctype's fields, for the builder's pickers. */
  /** Which channels this Genie can send on: a message step's choices. */
  channelStatus: () => request<{ sending: string[] }>("/api/admin/channels/status"),
  /** WhatsApp templates registered for this site, by Genie's id for each. */
  whatsappTemplates: () => request<{ templates: Record<string, WhatsAppTemplate> }>("/api/admin/channels/whatsapp-templates"),
  /** The whole registry, replaced. */
  saveWhatsappTemplates: (templates: Record<string, WhatsAppTemplate>) =>
    request<{ templates: Record<string, WhatsAppTemplate> }>("/api/admin/channels/whatsapp-templates", { method: "PUT", body: templates }),
  builderFields: (doctype: string) =>
    request<{ doctype: string; fields: { fieldname: string; label: string; type: string }[] }>(`/api/admin/agent-builder/site/fields?doctype=${encodeURIComponent(doctype)}`),
  /** Describe the agent, or a change, and get the whole graph back revised. Nothing is saved. */
  composeAgentBlueprint: (id: string, instruction: string, history: { instruction: string; note: string }[] = [], focus?: string) =>
    request<{ meta: BlueprintMeta; draft: BuilderGraph; note: string; problems: GraphProblem[]; outside?: string[] }>(`/api/admin/agent-builder/${encodeURIComponent(id)}/compose`, {
      method: "POST",
      body: { instruction, history, ...(focus ? { focus } : {}) },
      timeoutMs: 180_000,
    }),
  /** Chat with Genie: an answer about a running automation or agent, and what would change, as before → after. Nothing is saved. */
  reviseRunning: (kind: "automation" | "agent" | "playbook", id: string, instruction: string, history: { instruction: string; note: string }[] = [], scope?: string) =>
    request<Revision>(`/api/${kind === "automation" ? "automations" : kind === "agent" ? "agents" : "skills"}/${encodeURIComponent(id)}/revise`, {
      method: "POST",
      body: { instruction, history, ...(scope ? { scope } : {}) },
      timeoutMs: 180_000,
    }),
  /** Apply what was shown; the same checks as the edit form run again. A playbook's change is saved as a draft beside it. */
  applyRevision: (kind: "automation" | "agent" | "playbook", id: string, patch: Record<string, unknown>) =>
    request<unknown>(`/api/${kind === "automation" ? "automations" : kind === "agent" ? "agents" : "skills"}/${encodeURIComponent(id)}/revise/apply`, { method: "POST", body: { patch } }),
  /** Customise: a catalogue agent or a template as a new draft of your own; the original is untouched. A template comes over as Rules or as a Genie Agent. */
  customiseBlueprint: (from: "agent" | "template", id: string, as?: "rules" | "genie") =>
    request<Customised>("/api/admin/agent-builder/customise", { method: "POST", body: { from, id, ...(as ? { as } : {}) } }),
  /** What the draft, as a rule, would do to a named record — or to each record a schedule reads. Nothing is written. */
  tryAgentBlueprint: (id: string, input: { name?: string; asNew?: boolean }) =>
    request<TryResult>(`/api/admin/agent-builder/${encodeURIComponent(id)}/try`, { method: "POST", body: input, timeoutMs: 60_000 }),
  /** Run in ERPNext: the verdicts, the site's readiness, and the installed rule with its runs. */
  agentBlueprintErpnext: (id: string) => request<ErpnextStatus>(`/api/admin/agent-builder/${encodeURIComponent(id)}/erpnext`, { timeoutMs: 60_000 }),
  /** Install the published version on the site — or an earlier one, to roll back. */
  installAgentBlueprintErpnext: (id: string, version?: number) =>
    request<{ install: ErpnextStatus["install"] }>(`/api/admin/agent-builder/${encodeURIComponent(id)}/erpnext/install`, { method: "POST", body: version ? { version } : {}, timeoutMs: 60_000 }),
  agentBlueprintErpnextAction: (id: string, what: "pause" | "resume" | "remove") =>
    request<{ install: ErpnextStatus["install"] }>(`/api/admin/agent-builder/${encodeURIComponent(id)}/erpnext/${what}`, { method: "POST", timeoutMs: 60_000 }),
  publishAgentBlueprint: (id: string) => request<{ version: number; agentId: string }>(`/api/admin/agent-builder/${encodeURIComponent(id)}/publish`, { method: "POST" }),
  retireAgentBlueprint: (id: string) => request<{ ok: true }>(`/api/admin/agent-builder/${encodeURIComponent(id)}/retire`, { method: "POST" }),
  deleteAgentBlueprint: (id: string) => request<{ ok: true }>(`/api/admin/agent-builder/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /* -- agents ---------------------------------------------------------- */

  agentGallery: () => request<AgentGallery>("/api/agents/gallery"),
  agentDef: (id: string) => request<AgentDefDetail>(`/api/agents/defs/${encodeURIComponent(id)}`),
  agents: () => request<{ agents: AgentInstance[] }>("/api/agents"),
  agentInbox: () => request<{ proposals: AgentProposal[] }>("/api/agents/inbox"),
  installAgent: (config: AgentInstallConfig) =>
    request<{ instance: AgentInstance; skippedSenses: string[] }>("/api/agents", { method: "POST", body: config }),
  agent: (id: string) => request<AgentDetail>(`/api/agents/${encodeURIComponent(id)}`),
  updateAgent: (id: string, config: Partial<AgentInstallConfig>) =>
    request<AgentInstance>(`/api/agents/${encodeURIComponent(id)}`, { method: "PATCH", body: config }),
  /** What a file an agent's model made holds, for the approval card's preview. */
  artifactPreview: (id: string) =>
    request<{ id: string; name: string; sizeBytes: number; preview: FilePreview }>(`/api/agents/artifacts/${encodeURIComponent(id)}/preview`),
  /** Save one of a message's attachments — fetched with the session, then handed to the browser. */
  downloadInboxAttachment: async (id: string, attachment: InboxAttachment): Promise<void> => {
    const response = await fetch(
      apiUrl(`/api/inbox/${encodeURIComponent(id)}/attachment?fileUrl=${encodeURIComponent(attachment.fileUrl)}`),
      { headers: token.value ? { Authorization: `Bearer ${token.value}` } : {} }
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new ApiError(body?.message ?? `The file could not be downloaded (${response.status}).`, response.status);
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = attachment.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  /** Save a file an agent's model made (a deck) — fetched with the session, then handed to the browser. */
  downloadArtifact: async (id: string, fileName: string): Promise<void> => {
    const response = await fetch(apiUrl(`/api/agents/artifacts/${encodeURIComponent(id)}`), { headers: token.value ? { Authorization: `Bearer ${token.value}` } : {} });
    if (!response.ok) throw new ApiError(response.status === 404 ? "That file is no longer available." : `The file could not be downloaded (${response.status}).`, response.status);
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
  /** A built agent onto the latest version its administrator published. */
  upgradeAgent: (id: string) => request<AgentInstance>(`/api/agents/${encodeURIComponent(id)}/upgrade`, { method: "POST" }),
  agentAction: (id: string, action: "pause" | "resume" | "rehearse") =>
    request<AgentInstance>(`/api/agents/${encodeURIComponent(id)}/${action}`, { method: "POST" }),
  /** A real run, with the model: given room. */
  runAgent: (id: string) =>
    request<{ run: AgentRun; proposals: AgentProposal[]; notes: AgentNote[] }>(`/api/agents/${encodeURIComponent(id)}/run`, { method: "POST", timeoutMs: 300_000 }),
  rehearseAgent: (id: string) =>
    request<{ run: AgentRun; proposals: AgentProposal[]; notes: AgentNote[] }>(`/api/agents/${encodeURIComponent(id)}/dry-run`, { method: "POST", timeoutMs: 300_000 }),
  removeAgent: (id: string) => request<{ ok: true }>(`/api/agents/${encodeURIComponent(id)}`, { method: "DELETE" }),
  agentRuns: (id: string, before?: number) =>
    request<{ runs: AgentRun[]; more: boolean }>(`/api/agents/${encodeURIComponent(id)}/runs${before ? `?before=${before}` : ""}`),
  /** The same day again under the mandate as it is now: nothing recorded, nothing sent. */
  replayAgentRun: (id: string, runId: string) =>
    request<{ run: AgentRun; proposals: AgentProposal[]; notes: AgentNote[] }>(`/api/agents/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}/replay`, { method: "POST", timeoutMs: 300_000 }),
  agentProposals: (id: string, before?: number) =>
    request<{ proposals: AgentProposal[]; more: boolean }>(`/api/agents/${encodeURIComponent(id)}/proposals${before ? `?before=${before}` : ""}`),
  agentMessages: (id: string) => request<{ sent: OutboundMessage[]; received: InboundMessage[] }>(`/api/agents/${encodeURIComponent(id)}/messages`),
  decideProposal: (proposalId: string, decision: AgentDecision, reply?: string) =>
    request<AgentProposal>(`/api/agents/proposals/${encodeURIComponent(proposalId)}/decide`, { method: "POST", body: { decision, reply: reply || null }, timeoutMs: 60_000 }),
  replyToProposal: (proposalId: string, reply: string) =>
    request<AgentProposal>(`/api/agents/proposals/${encodeURIComponent(proposalId)}/reply`, { method: "POST", body: { reply } }),
  addAgentNote: (id: string, note: { kind: AgentNoteKind; about?: string | null; text: string }) =>
    request<AgentNote>(`/api/agents/${encodeURIComponent(id)}/notes`, { method: "POST", body: note }),
  editAgentNote: (noteId: string, text: string) =>
    request<AgentNote>(`/api/agents/notes/${encodeURIComponent(noteId)}`, { method: "PATCH", body: { text } }),
  removeAgentNote: (noteId: string) => request<{ ok: true }>(`/api/agents/notes/${encodeURIComponent(noteId)}`, { method: "DELETE" }),

  /* -- channels -------------------------------------------------------- */

  channelEndpoints: () =>
    request<{ endpoints: ChannelEndpoint[]; available: { kind: ChannelKind; label: string; sendable: boolean }[] }>("/api/channels/endpoints"),
  addChannelEndpoint: (input: { kind: ChannelKind; address: string; label?: string | null }) =>
    request<{ endpoint: ChannelEndpoint; verificationSent: boolean; verification: string | null; sentToNumber?: boolean }>("/api/channels/endpoints", { method: "POST", body: input }),
  verifyChannelEndpoint: (id: string, code: string) =>
    request<ChannelEndpoint>(`/api/channels/endpoints/${encodeURIComponent(id)}/verify`, { method: "POST", body: { code } }),
  setChannelEndpoint: (id: string, prefs: Partial<Pick<ChannelEndpoint, "label" | "forBriefings" | "forNudges" | "forEscalations" | "quietFrom" | "quietTo">>) =>
    request<ChannelEndpoint>(`/api/channels/endpoints/${encodeURIComponent(id)}`, { method: "PATCH", body: prefs }),
  removeChannelEndpoint: (id: string) => request<{ ok: true }>(`/api/channels/endpoints/${encodeURIComponent(id)}`, { method: "DELETE" }),
  channelPolicy: () => request<ChannelPolicy>("/api/admin/channels/policy"),
  saveChannelPolicy: (patch: Partial<ChannelPolicy>) => request<ChannelPolicy>("/api/admin/channels/policy", { method: "PATCH", body: patch }),

  /* -- connected apps -------------------------------------------------- */

  integrations: () => request<IntegrationOverview>("/api/integrations"),
  /** The browser navigates here; the session token rides once, in the query, and never in the redirect. */
  integrationStartUrl: (provider: IntegrationProvider, scopes: IntegrationScope[]) =>
    `/api/integrations/${encodeURIComponent(provider)}/start?token=${encodeURIComponent(token.value ?? "")}&scopes=${encodeURIComponent(scopes.join(","))}`,
  revokeIntegration: (id: string) => request<{ ok: true }>(`/api/integrations/${encodeURIComponent(id)}/revoke`, { method: "POST" }),
  removeIntegration: (id: string) => request<{ ok: true }>(`/api/integrations/${encodeURIComponent(id)}`, { method: "DELETE" }),
  integrationFolders: (id: string, parent?: string) =>
    request<{ folders: { id: string; name: string }[] }>(`/api/integrations/${encodeURIComponent(id)}/folders${parent ? `?parent=${encodeURIComponent(parent)}` : ""}`),
  setIntegrationFolders: (id: string, folders: { id: string; name: string; purpose: "intake" | "knowledge" }[]) =>
    request<IntegrationConnection>(`/api/integrations/${encodeURIComponent(id)}/folders`, { method: "PUT", body: { folders } }),

  /* -- automations ---------------------------------------------------- */

  /**
   * The gallery, the installed list and everything in between.
   *
   * All of it server-side now. The catalogue used to be a 657-line constant in
   * this bundle and the install state a localStorage key, which meant an
   * automation existed only in the browser that created it — fine for a badge,
   * useless for something that has to run at 06:00 while nobody has a tab open.
   */
  automationGallery: () => request<AutomationGallery>("/api/automations/gallery"),

  automationTemplate: (id: string) =>
    request<AutomationTemplateDetail>(`/api/automations/templates/${encodeURIComponent(id)}`),

  automationPreview: (config: AutomationConfig) =>
    request<AutomationPreview>("/api/automations/preview", { method: "POST", body: config }),

  /** Genuinely queries the ERP, so it is given room to take its time. */
  automationDryRun: (config: AutomationConfig) =>
    request<AutomationPreview & { result: AutomationRunResult }>("/api/automations/dry-run", {
      method: "POST",
      body: config,
      timeoutMs: 90_000,
    }),

  /**
   * What goes in a dropdown. One endpoint for doctypes, records and fields,
   * because they differ only in what they ask the site.
   */
  automationOptions: (params: {
    kind: "doctype" | "record" | "field";
    doctype?: string;
    q?: string;
    only?: "date" | "number" | "link";
  }) => {
    const search = new URLSearchParams({ kind: params.kind });
    if (params.doctype) search.set("doctype", params.doctype);
    if (params.q) search.set("q", params.q);
    if (params.only) search.set("only", params.only);
    return request<{ options: LookupOption[]; error?: string }>(
      `/api/automations/options?${search.toString()}`
    );
  },

  /**
   * What a plain-English description finds. Pure ranking over the catalogue —
   * no ERP call — so it is cheap enough to run on every pause in typing.
   */
  automationDescribe: (q: string) =>
    request<{ query: string; examples: string[]; matches: DescribedCard[] }>(
      `/api/automations/describe?q=${encodeURIComponent(q)}`
    ),

  /* -- the Desk ------------------------------------------------------- */

  deskDashboards: () =>
    request<{ modules: string[]; dashboards: DeskDashboard[] }>("/api/desk/dashboards"),

  /** One dashboard, built the same way the assistant builds one. */
  deskDashboard: (dashboard: string, opts: { period?: string } = {}) =>
    request<{ dashboard: DashboardPayload }>("/api/desk/dashboard", {
      method: "POST",
      body: { dashboard, ...opts },
      timeoutMs: 45_000,
    }),

  /** The Desk's own figures — owed, due out, the year's shape. Cached server-side. */
  deskPulse: () => request<DeskPulse>("/api/desk/pulse", { timeoutMs: 45_000 }),

  deskList: (body: {
    doctype: string;
    filters?: Record<string, unknown>;
    fields?: string[];
    order_by?: string;
    limit?: number;
  }) =>
    request<{ doctype: string; rows: Record<string, unknown>[] }>("/api/desk/list", {
      method: "POST",
      body,
      timeoutMs: 30_000,
    }),

  automations: () => request<{ automations: Automation[] }>("/api/automations"),

  automationInstall: (config: AutomationConfig) =>
    request<Automation>("/api/automations", { method: "POST", body: config }),

  automationUpdate: (id: string, config: Partial<AutomationConfig>) =>
    request<Automation>(`/api/automations/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: config,
    }),

  automationDetail: (id: string) =>
    request<Automation & { runs: AutomationRun[] }>(`/api/automations/${encodeURIComponent(id)}`),

  automationRuns: (id: string) =>
    request<{ runs: AutomationRun[] }>(`/api/automations/${encodeURIComponent(id)}/runs`),

  automationPause: (id: string) =>
    request<Automation>(`/api/automations/${encodeURIComponent(id)}/pause`, { method: "POST" }),

  automationResume: (id: string) =>
    request<Automation>(`/api/automations/${encodeURIComponent(id)}/resume`, { method: "POST" }),

  automationRunNow: (id: string) =>
    request<{ run: AutomationRun }>(`/api/automations/${encodeURIComponent(id)}/run`, {
      method: "POST",
      timeoutMs: 90_000,
    }),

  /** Put back what one run changed. Only bulk edits leave anything to put back. */
  automationUndo: (runId: string) =>
    request<{ restored: string[]; failed: { name: string; why: string }[]; remaining: number }>(
      `/api/automations/runs/${encodeURIComponent(runId)}/undo`,
      { method: "POST", timeoutMs: 90_000 }
    ),

  automationRemove: (id: string) =>
    request<{ ok: true }>(`/api/automations/${encodeURIComponent(id)}`, { method: "DELETE" }),

  reset: () => request<{ ok: true }>("/api/reset", { method: "POST" }),

  /**
   * Revoke the refresh token server-side. Signing out used to be purely local
   * — the tokens were dropped from this browser and the server was never told
   * — so the refresh token stayed valid for its whole TTL (30 days by
   * default) after the operator had signed out. That is precisely the window
   * rotation was introduced to close, so closing it here matters more than
   * the button being tidy.
   *
   * Deliberately unauthenticated: the access token has usually expired by the
   * time anyone signs out, and the refresh token in the body is the thing
   * being revoked. The route answers 200 whatever happens.
   */
  signOut: (refresh: string) =>
    request<{ ok: true }>("/api/auth/signout", {
      method: "POST",
      body: { refreshToken: refresh },
      auth: false,
    }),
};

/**
 * End the session: revoke it on the server, then forget it here.
 *
 * One helper rather than each caller's own three lines, because a caller that
 * forgets the revoke leaves a live refresh token behind and nothing visible
 * says so. The local clear runs even when the revoke fails — a browser that
 * still believes it is signed in is the worse outcome, and an unreachable
 * server cannot be talked into forgetting anything anyway.
 */
export async function endSession(): Promise<void> {
  const refresh = refreshToken.value;
  if (refresh) {
    try {
      await api.signOut(refresh);
    } catch {
      // Sign-out never fails loudly: see above.
    }
  }
  setSession(null);
  currentUser.value = null;
}

export type AgentId = "codex" | "claude";
export type AgentFallback = AgentId | "none";
/** One level of the choice; null means "not decided here, follow the level above". */
export interface AgentLayer {
  primary: AgentId | null;
  fallback: AgentFallback | null;
  codexModel: string | null;
  claudeModel: string | null;
  /** Answer with OpenClaw first. Absent or null: follow the level above. */
  openclaw?: boolean | null;
}
export interface AgentModelChoice {
  id: string;
  name: string;
}
/** Chat and automations (the builder, agent runs) have separate choices. */
export type AgentScope = "chat" | "automation";

export interface AgentSettings {
  scope: AgentScope;
  /** What this person's messages use now. */
  primary: AgentId;
  fallback: AgentFallback;
  /** Which level each half came from. */
  source: { primary: "user" | "site" | "default"; fallback: "user" | "site" | "default"; codexModel: "user" | "site" | "default"; claudeModel: "user" | "site" | "default" };
  /** What each agent can run. Codex's list is the backend's for this account (null when it cannot say); Claude's is suggestions, and any id may be typed. */
  models: {
    codex: { list: AgentModelChoice[] | null; default: string };
    claude: { list: AgentModelChoice[]; default: string; custom: boolean };
  };
  user: AgentLayer;
  site: AgentLayer;
  /** Whether this person may set the site's default. */
  canManageSite: boolean;
  defaults: { primary: AgentId; fallback: AgentFallback };
  /** Whether this server can run each agent right now. */
  available: { codex: boolean; claude: boolean; openclaw: boolean };
  /** Whether OpenClaw answers first for this person now, and which level decided it. */
  openclaw: boolean;
  openclawSource: "user" | "site" | "default";
}
